import { Injectable } from '@angular/core';
import { User } from '@mitel/cloudlink-sdk';
import { Subject } from 'rxjs';
import { AdminService } from './admin.service';
import { MiVBProvisioningService } from './mivb-provisioning.service';

interface MiVBUserTelephonyService {
  serviceAlias: string,
  hostedUserServiceType: string,
  hostedUserServiceClassType: string,
  hostedUserServiceTypeName: string,
  hostedUserServiceClassTypeName: string,
  serviceLabel: string,
  serviceDetails?: any
};

export interface FormattedMiVBUserTelephonyService {
  serviceAlias: string,
  primary: string,
  secondaries: string[],
  serviceDetails: { uccBundleName: string }
};

@Injectable({
  providedIn: 'root'
})
export class MiVBUserService {
  locDepChanged: Subject<any> = new Subject<any>();
  settingsCleared: Subject<void> = new Subject<void>();
  constructor(
    private adminService: AdminService,
    private miVBProvisioning: MiVBProvisioningService
  ) { }

  public getMiVBUserDetails(user: User) {
    let promises = [];
    if (!user.accountId || !user.userId) {
      return;
    }

    if (user.pbxUserId) {
      let miVBUserPromise = this.miVBProvisioning.getMiVBUser(user.pbxUserId)
        .catch(err => {
          console.log("Failed to fetch the MiVB details for user", err);
          return null;
        });
      promises.push(miVBUserPromise);
    } else {
      promises.push(Promise.resolve(null));
    }
    // only call user tags if it already doesn't exist in parameter object
    if (user.tags) {
      promises.push(Promise.resolve(user.tags));
    } else {
      let templateDataPromise = this.adminService.getUserTagsByAccountIdWithoutSpinner(user.accountId, user.userId)
        .catch(error => {
          console.log("Failed to fetch the tags for user", error);
          return {};
        });
      promises.push(templateDataPromise);
    }

    return Promise.all(promises).then(values => {
      return { ...values[1], "mivbConfiguration": values[0] };
    });
  }

  public getMiVBUserDetailsLiveSync(user: User) {
    let promises = [];
    if (!user.accountId || !user.userId) {
      return;
    }

    if (user.pbxUserId) {
      let miVBUserPromise = this.miVBProvisioning.getMiVBUserLiveSync(user.pbxUserId)
        .then(res => {
          this.locDepChanged.next({ location: res?.location, department: res?.department });
          return res;
        })
        .catch(err => {
          console.log("Failed to fetch the live synced MiVB details for user", err);
          return null;
        });
      promises.push(miVBUserPromise);
    } else {
      promises.push(Promise.resolve(null));
    }
    let templateDataPromise = this.adminService.getUserTagsByAccountIdWithoutSpinner(user.accountId, user.userId)
      .catch(error => {
        console.log("Failed to fetch the tags for user", error);
        return {};
      });
    promises.push(templateDataPromise);

    return Promise.all(promises).then(values => {
      return { ...values[1], "mivbConfiguration": values[0] };
    });
  }

  public getMiVBUserServices(user: User): Promise<FormattedMiVBUserTelephonyService[] | null> {
    if (!user.accountId || !user.userId) {
      return Promise.resolve(null);
    }

    if (user.pbxUserId) {
      return this.miVBProvisioning.getMiVBUserServices(user.pbxUserId).then(services =>{
        let formattedServices: FormattedMiVBUserTelephonyService[];
        formattedServices = services && services.userServices? this.getFormattedUserServices(services.userServices) : null;
        return formattedServices;
      })
      .catch(err => {
        console.log("Failed to fetch the user's services", err);
        return null;
      });
    }
    else{
      return Promise.resolve(null);
    }
  }

  private getFormattedUserServices(rawServices: MiVBUserTelephonyService[]): FormattedMiVBUserTelephonyService[]{
    let formattedServices: FormattedMiVBUserTelephonyService[] = [];
    
    rawServices.sort((a,b) => parseInt(a.hostedUserServiceClassType) - parseInt(b.hostedUserServiceClassType));

    rawServices.forEach(service =>{
      if(service.serviceAlias !== ''){
        let parentService = formattedServices.find((fService => fService.serviceAlias === service.serviceAlias));
        if(!parentService){
          //create the parentService
          parentService = {
            serviceAlias: service.serviceAlias,
            primary: undefined,
            secondaries: [],
            serviceDetails: service.serviceDetails ? { uccBundleName: service.serviceDetails.uccBundleName } : { uccBundleName: '' }
          };
          formattedServices.push(parentService);
        }
        if(parentService.primary){
          //primary line already exists, so add it as a secondary line
          parentService.secondaries.push(this.getSecondaryServiceString(service));
        }
        else{
          //it should become the primary since the services were sorted to ascend in hostedUserServiceClassType
          parentService.primary = this.getPrimaryServiceString(service);
        }
      }
    });
  
    return formattedServices;
  }

  private getPrimaryServiceString(service: MiVBUserTelephonyService): string {
    let formattedString: string;
    if(parseInt(service.hostedUserServiceClassType) < 100){
      formattedString = formattedString = "Extension: " + service.serviceAlias + " " + service.serviceLabel + " (" + service.hostedUserServiceTypeName + ")";
    }
    else{
      formattedString = service.serviceLabel + " (" + service.hostedUserServiceClassTypeName + ")";
    }
    return formattedString;
  }

  private getSecondaryServiceString(service: MiVBUserTelephonyService): string {
    let formattedString: string
    if(parseInt(service.hostedUserServiceClassType) < 100){
      formattedString =  service.serviceLabel + " (" + service.hostedUserServiceTypeName + ")";
    }
    else{
      formattedString = service.serviceLabel + " (" + service.hostedUserServiceClassTypeName + ")";
    }
    return formattedString;
  }

  public async deleteMiVBUser(user: User, tags: any) {
    let tagBody = {
      products: []
    };
    if (tags.products && tags.products.length > 0 && tags.products.indexOf('mivb') > -1) {
      let ndx = tags.products.indexOf('mivb');
      tagBody.products.push(...tags.products);
      tagBody.products.splice(ndx, 1);
    }
    if (tags.hasOwnProperty('license')) {
      this.adminService.deleteUserTagByAccountId(user.accountId, user.userId, 'license');
    }
    //Check is user actually exists on MiVB with an extension. If so, remove the user, this will remove the extension and services
    if (user.pbxUserId && user.pbxUserId !== "" && user.extension && user.extension !== "") {
      await this.miVBProvisioning.deleteMiVBUser(user.pbxUserId)
        .then(() => {
          console.log("Deleted MiVB user successfully");

          //delete mivb -> deploymentEmailHistory tags if applicable
          if (tags.hasOwnProperty('mivb')) {
            this.adminService.deleteUserTagByAccountId(user.accountId, user.userId, 'mivb').then(() => {
              console.log("Deleted deploymentEmailHistory tags successfully");
            }).catch(err => {
              console.warn("Failed to delete deploymentEmailHistory tags: ", err);
            });
          }
          
          this.settingsCleared.next();
          return true;
        }).catch(err => {
          if (err.statusCode && err.statusCode == 404) {
            //User does not exist on the mivb - not necessarily a problem
            console.warn("Failed to delete MiVB user: user not found");
          }
          else {
            // Something went wrong
            // Throw error so that the caller can handle it
            // and to break the promise chain/stop the function
            throw(err);
          }
        });
    }

    // Update the tags for the user if no error was thrown when deleting the user
    return this.adminService.tryUpdateTagsOfUserByAccountId(user.accountId, user.userId, tagBody, tags)
      .then(updatedTags => console.log("Updated tags for user successfully", updatedTags))
      .catch(err => console.warn("Failed to clear mivb tags", err));
  }

  /**
   * Calls deleteMiVBUser and deletes the user from the micro/the solution without making changes to the cloudlink user
   * @param user The user object from the admin micro
   */
  public deleteMiVBUserOnly(user: User) {
    if(user.pbxUserId) {
      return this.miVBProvisioning.deleteMiVBUserAsyncWithPolling(user.accountId, user.userId, user.pbxUserId).catch(err => {
        // For some reason the micro returns a 400 and not a 404 if the user does not exist. Raised: https://mitel.atlassian.net/browse/CLSD-2878
        // We will look for both and handle it.
        if (err.statusCode == 404 || (err.statusCode == 400 && err.body?.message?.includes('cannot find user'))) {
          // User does not exist on the mivb - not necessarily a problem - async delete job doesn't even start. Does not return a jobId
          // Or getUserName failed with the 404 or 400 - user was removed from Admin
          console.warn("Could not delete MiVB user: user not found. User may already be deleted.", err);
        } else {
          console.warn("Failed to delete MiVB user", err);
          throw(err)
        }
      });
    } else {
      console.warn("Failed to delete MiVB user: user did not have pbxUserId");
      throw(new Error("No pbxUserId"))
    }
  }
}
